import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  FiSettings, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiChevronDown,
  FiChevronRight, FiZap, FiClock, FiUsers, FiLayers, FiSliders,
  FiRefreshCw, FiSave, FiAlertCircle, FiToggleLeft, FiToggleRight,
  FiCopy, FiSearch, FiFilter, FiGrid, FiList
} from 'react-icons/fi';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORY COLORS
// ═══════════════════════════════════════════════════════════════════════════
const CATEGORY_COLORS = {
  'Placement': { bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-200', icon: '📦' },
  'Driver Acquisition': { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', icon: '🚗' },
  'Pre-Delivery': { bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-200', icon: '📋' },
  'Day-Of': { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200', icon: '☀️' },
  'Admin': { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200', icon: '👑' },
  'Support': { bg: 'bg-cyan-50', text: 'text-cyan-700', ring: 'ring-cyan-200', icon: '💬' },
  'Exception': { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200', icon: '⚠️' },
  'Fulfillment': { bg: 'bg-indigo-50', text: 'text-indigo-700', ring: 'ring-indigo-200', icon: '✅' },
  'Quality': { bg: 'bg-pink-50', text: 'text-pink-700', ring: 'ring-pink-200', icon: '🔍' },
};

const PRIORITY_STYLES = {
  P0: 'bg-red-600 text-white',
  P1: 'bg-amber-500 text-white',
  P2: 'bg-gray-500 text-white',
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function TicketTypeAdmin() {
  const { adminInfo, token } = useSelector(state => state.login);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [editingType, setEditingType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [savingWeights, setSavingWeights] = useState(false);
  const [modifiedWeights, setModifiedWeights] = useState({});

  // Fetch ticket types
  const fetchTicketTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/v1/ticket-types`, {
        params: { includeInactive: showInactive ? 'true' : 'false' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketTypes(response.data.ticketTypes || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching ticket types:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, showInactive]);

  useEffect(() => {
    fetchTicketTypes();
  }, [fetchTicketTypes]);

  // Seed default types
  const seedDefaults = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/v1/ticket-types/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTicketTypes();
    } catch (err) {
      console.error('Error seeding defaults:', err);
      setError(err.message);
    }
  };

  // Update weight
  const handleWeightChange = (ticketTypeId, weight) => {
    setModifiedWeights(prev => ({
      ...prev,
      [ticketTypeId]: Math.max(1, Math.min(10, parseInt(weight) || 1))
    }));
  };

  // Save weights
  const saveWeights = async () => {
    if (Object.keys(modifiedWeights).length === 0) return;
    
    try {
      setSavingWeights(true);
      await axios.patch(`${API_BASE}/api/v1/ticket-types/weights`, {
        weights: modifiedWeights
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModifiedWeights({});
      await fetchTicketTypes();
    } catch (err) {
      console.error('Error saving weights:', err);
      setError(err.message);
    } finally {
      setSavingWeights(false);
    }
  };

  // Toggle active status
  const toggleActive = async (ticketType) => {
    try {
      if (ticketType.isActive) {
        await axios.delete(`${API_BASE}/api/v1/ticket-types/${ticketType.ticketTypeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE}/api/v1/ticket-types/${ticketType.ticketTypeId}/reactivate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      await fetchTicketTypes();
    } catch (err) {
      console.error('Error toggling active:', err);
      setError(err.message);
    }
  };

  // Filter ticket types
  const filteredTypes = useMemo(() => {
    let filtered = ticketTypes;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.ticketTypeId?.toLowerCase().includes(q) ||
        t.ticketName?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
      );
    }
    
    if (!showInactive) {
      filtered = filtered.filter(t => t.isActive);
    }
    
    return filtered.sort((a, b) => (a.displayOrder || 100) - (b.displayOrder || 100));
  }, [ticketTypes, selectedCategory, searchQuery, showInactive]);

  // Group by category
  const groupedTypes = useMemo(() => {
    const groups = {};
    filteredTypes.forEach(type => {
      const cat = type.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(type);
    });
    return groups;
  }, [filteredTypes]);

  const categories = useMemo(() => {
    const cats = [...new Set(ticketTypes.map(t => t.category))].filter(Boolean);
    return ['all', ...cats];
  }, [ticketTypes]);

  const toggleSection = (category) => {
    setExpandedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (loading && ticketTypes.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <FiRefreshCw className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading ticket types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <FiSettings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  Ticket Type Configuration
                </h1>
                <p className="text-sm text-slate-400">
                  Manage triggers, priorities, weights, and UI for each ticket type
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {Object.keys(modifiedWeights).length > 0 && (
                <button
                  onClick={saveWeights}
                  disabled={savingWeights}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/25"
                >
                  {savingWeights ? (
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <FiSave className="w-4 h-4" />
                  )}
                  Save Weights ({Object.keys(modifiedWeights).length})
                </button>
              )}

              <button
                onClick={seedDefaults}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-xl text-sm font-semibold transition-colors"
              >
                <FiRefreshCw className="w-4 h-4" />
                Seed Defaults
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/25"
              >
                <FiPlus className="w-4 h-4" />
                Add Type
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 mt-4">
            <div className="relative flex-1 max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search ticket types..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                className="rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
              />
              Show Inactive
            </label>

            <div className="flex items-center bg-slate-800 rounded-xl p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <FiGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <FiList className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="flex items-center gap-3 px-4 py-3 bg-red-900/50 border border-red-700 rounded-xl">
            <FiAlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-sm text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {ticketTypes.length === 0 ? (
          <EmptyState onSeed={seedDefaults} />
        ) : viewMode === 'cards' ? (
          <CardView
            groupedTypes={groupedTypes}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            modifiedWeights={modifiedWeights}
            onWeightChange={handleWeightChange}
            onEdit={setEditingType}
            onToggleActive={toggleActive}
          />
        ) : (
          <TableView
            types={filteredTypes}
            modifiedWeights={modifiedWeights}
            onWeightChange={handleWeightChange}
            onEdit={setEditingType}
            onToggleActive={toggleActive}
          />
        )}
      </div>

      {/* Edit Modal */}
      {editingType && (
        <EditTicketTypeModal
          ticketType={editingType}
          onClose={() => setEditingType(null)}
          onSave={async (updated) => {
            try {
              await axios.patch(`${API_BASE}/api/v1/ticket-types/${updated.ticketTypeId}`, updated, {
                headers: { Authorization: `Bearer ${token}` }
              });
              await fetchTicketTypes();
              setEditingType(null);
            } catch (err) {
              setError(err.message);
            }
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTicketTypeModal
          onClose={() => setShowCreateModal(false)}
          onSave={async (newType) => {
            try {
              await axios.post(`${API_BASE}/api/v1/ticket-types`, newType, {
                headers: { Authorization: `Bearer ${token}` }
              });
              await fetchTicketTypes();
              setShowCreateModal(false);
            } catch (err) {
              setError(err.message);
            }
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CARD VIEW
// ═══════════════════════════════════════════════════════════════════════════
function CardView({ groupedTypes, expandedSections, toggleSection, modifiedWeights, onWeightChange, onEdit, onToggleActive }) {
  return (
    <div className="space-y-6">
      {Object.entries(groupedTypes).map(([category, types]) => {
        const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Placement'];
        const isExpanded = expandedSections[category] !== false;

        return (
          <div key={category} className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
            <button
              onClick={() => toggleSection(category)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-700/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{colors.icon}</span>
                <h2 className="text-lg font-bold text-white">{category}</h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                  {types.length} types
                </span>
              </div>
              {isExpanded ? (
                <FiChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <FiChevronRight className="w-5 h-5 text-slate-400" />
              )}
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {types.map(type => (
                  <TicketTypeCard
                    key={type.ticketTypeId}
                    type={type}
                    modifiedWeight={modifiedWeights[type.ticketTypeId]}
                    onWeightChange={onWeightChange}
                    onEdit={onEdit}
                    onToggleActive={onToggleActive}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TICKET TYPE CARD
// ═══════════════════════════════════════════════════════════════════════════
function TicketTypeCard({ type, modifiedWeight, onWeightChange, onEdit, onToggleActive }) {
  const weight = modifiedWeight ?? type.weight ?? 1;
  const isModified = modifiedWeight !== undefined;

  return (
    <div className={`
      bg-slate-900/50 rounded-xl border transition-all
      ${type.isActive ? 'border-slate-700/50 hover:border-slate-600' : 'border-slate-800 opacity-60'}
    `}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${PRIORITY_STYLES[type.priorityDefault] || PRIORITY_STYLES.P2}`}>
                {type.priorityDefault}
              </span>
              {type.isAdminOnly && (
                <span className="px-2 py-0.5 rounded bg-rose-900/50 text-rose-300 text-xs font-semibold">
                  Admin
                </span>
              )}
              {!type.isActive && (
                <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-400 text-xs font-semibold">
                  Inactive
                </span>
              )}
            </div>
            <h3 className="font-semibold text-white truncate">{type.ticketName}</h3>
            <p className="text-xs text-slate-500 font-mono">{type.ticketTypeId}</p>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(type)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            >
              <FiEdit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onToggleActive(type)}
              className={`p-2 rounded-lg transition-colors ${
                type.isActive
                  ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {type.isActive ? <FiToggleRight className="w-4 h-4" /> : <FiToggleLeft className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-slate-400 line-clamp-2 mb-3">
          {type.description || 'No description'}
        </p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-slate-800/50 rounded-lg px-2 py-1.5 text-center">
            <p className="text-xs text-slate-500">Effort</p>
            <p className="text-sm font-semibold text-white">{type.effortMinutes || '—'}m</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg px-2 py-1.5 text-center">
            <p className="text-xs text-slate-500">Order</p>
            <p className="text-sm font-semibold text-white">#{type.displayOrder || '—'}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg px-2 py-1.5 text-center">
            <p className="text-xs text-slate-500">Parallel</p>
            <p className="text-sm font-semibold text-white">{type.canParallelize ? 'Yes' : 'No'}</p>
          </div>
        </div>

        {/* Weight Slider */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs text-slate-400 flex items-center gap-1">
              <FiSliders className="w-3 h-3" />
              Weight
            </label>
            <span className={`text-sm font-bold ${isModified ? 'text-amber-400' : 'text-white'}`}>
              {weight}
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            value={weight}
            onChange={e => onWeightChange(type.ticketTypeId, e.target.value)}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
          />
          <div className="flex justify-between text-[10px] text-slate-600">
            <span>Low</span>
            <span>High</span>
          </div>
        </div>

        {/* Trigger Preview */}
        {type.triggerCondition && (
          <div className="mt-3 p-2 bg-slate-800/50 rounded-lg">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Trigger</p>
            <p className="text-xs text-slate-300 font-mono line-clamp-2">{type.triggerCondition}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TABLE VIEW
// ═══════════════════════════════════════════════════════════════════════════
function TableView({ types, modifiedWeights, onWeightChange, onEdit, onToggleActive }) {
  return (
    <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Category</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Priority</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Weight</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Effort</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Flags</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {types.map(type => {
              const weight = modifiedWeights[type.ticketTypeId] ?? type.weight ?? 1;
              const isModified = modifiedWeights[type.ticketTypeId] !== undefined;
              const colors = CATEGORY_COLORS[type.category] || CATEGORY_COLORS['Placement'];

              return (
                <tr key={type.ticketTypeId} className={`hover:bg-slate-700/30 ${!type.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-semibold text-white">{type.ticketName}</p>
                      <p className="text-xs text-slate-500 font-mono">{type.ticketTypeId}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                      {type.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${PRIORITY_STYLES[type.priorityDefault]}`}>
                      {type.priorityDefault}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={weight}
                        onChange={e => onWeightChange(type.ticketTypeId, e.target.value)}
                        className={`w-14 px-2 py-1 rounded-lg text-center text-sm font-bold bg-slate-900 border ${
                          isModified ? 'border-amber-500 text-amber-400' : 'border-slate-700 text-white'
                        } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-slate-300">{type.effortMinutes || '—'}m</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {type.isAdminOnly && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-900/50 text-rose-300">Admin</span>
                      )}
                      {type.slackIntegration && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-900/50 text-purple-300">Slack</span>
                      )}
                      {type.canParallelize && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-900/50 text-cyan-300">∥</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => onEdit(type)}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onToggleActive(type)}
                        className={`p-2 rounded-lg transition-colors ${
                          type.isActive
                            ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30'
                            : 'text-slate-400 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        {type.isActive ? <FiToggleRight className="w-4 h-4" /> : <FiToggleLeft className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPTY STATE
// ═══════════════════════════════════════════════════════════════════════════
function EmptyState({ onSeed }) {
  return (
    <div className="text-center py-20">
      <div className="w-20 h-20 rounded-3xl bg-slate-800 flex items-center justify-center mx-auto mb-6">
        <FiLayers className="w-10 h-10 text-slate-500" />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">No Ticket Types Configured</h2>
      <p className="text-slate-400 mb-6 max-w-md mx-auto">
        Get started by seeding the default ticket types from your configuration.
      </p>
      <button
        onClick={onSeed}
        className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/25"
      >
        <FiPlus className="w-4 h-4" />
        Seed Default Ticket Types
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════
function EditTicketTypeModal({ ticketType, onClose, onSave }) {
  const [form, setForm] = useState({ ...ticketType });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-white">Edit Ticket Type</h2>
            <p className="text-sm text-slate-400 font-mono">{form.ticketTypeId}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <Section title="Basic Information">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Ticket Name" value={form.ticketName} onChange={v => updateField('ticketName', v)} />
              <Field label="Category" value={form.category} onChange={v => updateField('category', v)} type="select" options={Object.keys(CATEGORY_COLORS)} />
            </div>
            <Field label="Description" value={form.description} onChange={v => updateField('description', v)} type="textarea" />
          </Section>

          {/* Priority & Weight */}
          <Section title="Priority & Weight">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Default Priority" value={form.priorityDefault} onChange={v => updateField('priorityDefault', v)} type="select" options={['P0', 'P1', 'P2']} />
              <Field label="Weight (1-10)" value={form.weight} onChange={v => updateField('weight', parseInt(v) || 1)} type="number" min={1} max={10} />
              <Field label="Effort (minutes)" value={form.effortMinutes} onChange={v => updateField('effortMinutes', parseInt(v) || 0)} type="number" min={0} />
            </div>
            <Field label="Priority Logic" value={form.priorityLogic} onChange={v => updateField('priorityLogic', v)} placeholder="e.g., P0 if daysUntilDelivery <= 1" />
          </Section>

          {/* Trigger */}
          <Section title="Trigger Configuration">
            <Field label="Trigger Condition" value={form.triggerCondition} onChange={v => updateField('triggerCondition', v)} type="textarea" placeholder="Human-readable condition" />
            <Field label="Trigger Logic" value={form.triggerLogic} onChange={v => updateField('triggerLogic', v)} type="textarea" placeholder="Code/expression for evaluation" />
          </Section>

          {/* Behavior Flags */}
          <Section title="Behavior Flags">
            <div className="grid grid-cols-2 gap-4">
              <Toggle label="Can Parallelize" checked={form.canParallelize} onChange={v => updateField('canParallelize', v)} />
              <Toggle label="Requires Ownership" checked={form.requiresOwnership} onChange={v => updateField('requiresOwnership', v)} />
              <Toggle label="Auto Assign" checked={form.autoAssign} onChange={v => updateField('autoAssign', v)} />
              <Toggle label="Admin Only" checked={form.isAdminOnly} onChange={v => updateField('isAdminOnly', v)} />
              <Toggle label="Slack Integration" checked={form.slackIntegration} onChange={v => updateField('slackIntegration', v)} />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Max Concurrent per Order" value={form.maxConcurrentPerOrder} onChange={v => updateField('maxConcurrentPerOrder', parseInt(v) || 1)} type="number" min={1} />
              <Field label="Display Order" value={form.displayOrder} onChange={v => updateField('displayOrder', parseInt(v) || 100)} type="number" />
            </div>
          </Section>

          {/* Escalation */}
          <Section title="Escalation">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Escalation Rule" value={form.escalationRule} onChange={v => updateField('escalationRule', v)} />
              <Field label="Timeout (minutes)" value={form.escalationTimeoutMinutes} onChange={v => updateField('escalationTimeoutMinutes', parseInt(v) || null)} type="number" />
            </div>
          </Section>

          {/* Follow-up */}
          <Section title="Follow-up Tickets">
            <Field label="Creates Follow-up Ticket" value={form.createsFollowUpTicket} onChange={v => updateField('createsFollowUpTicket', v)} placeholder="Ticket type ID" />
            <Field label="Follow-up Condition" value={form.followUpCondition} onChange={v => updateField('followUpCondition', v)} />
          </Section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CREATE MODAL
// ═══════════════════════════════════════════════════════════════════════════
function CreateTicketTypeModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    ticketTypeId: '',
    ticketName: '',
    category: 'Placement',
    description: '',
    priorityDefault: 'P2',
    weight: 1,
    effortMinutes: 10,
    canParallelize: false,
    requiresOwnership: false,
    autoAssign: true,
    isAdminOnly: false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.ticketTypeId || !form.ticketName) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-slate-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white">Create Ticket Type</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <Field label="Ticket Type ID" value={form.ticketTypeId} onChange={v => updateField('ticketTypeId', v.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} placeholder="e.g., CUSTOM_TASK" required />
          <Field label="Ticket Name" value={form.ticketName} onChange={v => updateField('ticketName', v)} placeholder="Human-readable name" required />
          <Field label="Category" value={form.category} onChange={v => updateField('category', v)} type="select" options={Object.keys(CATEGORY_COLORS)} />
          <Field label="Description" value={form.description} onChange={v => updateField('description', v)} type="textarea" />
          <div className="grid grid-cols-3 gap-4">
            <Field label="Priority" value={form.priorityDefault} onChange={v => updateField('priorityDefault', v)} type="select" options={['P0', 'P1', 'P2']} />
            <Field label="Weight" value={form.weight} onChange={v => updateField('weight', parseInt(v) || 1)} type="number" min={1} max={10} />
            <Field label="Effort (min)" value={form.effortMinutes} onChange={v => updateField('effortMinutes', parseInt(v) || 0)} type="number" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/50">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-700">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form.ticketTypeId || !form.ticketName}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
            Create Type
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, options = [], required, ...props }) {
  const baseClass = "w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={baseClass} {...props}>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className={baseClass} {...props} />
      ) : (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseClass} {...props} />
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between p-3 bg-slate-800 rounded-xl cursor-pointer hover:bg-slate-700/50 transition-colors">
      <span className="text-sm text-slate-300">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-indigo-600' : 'bg-slate-700'}`}
      >
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'left-5' : 'left-1'}`} />
      </button>
    </label>
  );
}

